import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiFetchClient } from "@/lib/api/client";
import { defaultLocale, isSupportedLocale, keys } from "@/lib/i18n/portal";
import { useTranslations } from "@/lib/i18n/intl";
import { InviteAcceptForm } from "@/modules/invite/components/InviteAcceptForm";
import { InviteSuccessScreen } from "@/modules/invite/components/InviteSuccessScreen";
import {
  AuthBrandPanel,
  AuthMobileHeader,
  BrandDiamondBullet,
} from "@/shared/components/auth/AuthBrand";
import { endpoints } from "@/lib/api/endpoints";
import { getSellerLoginUrl } from "@/lib/auth/storeSlug";
import type { PublicInviteResponse } from "@/lib/api/types";

function getResolvedLocale(value: string | undefined) {
  return value && isSupportedLocale(value) ? value : defaultLocale;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

type InviteState =
  | { status: "loading" }
  | { status: "invalid" }
  | { status: "ready"; data: PublicInviteResponse };

export function InvitePage() {
  const { locale: routeLocale, token = "" } = useParams();
  const locale = getResolvedLocale(routeLocale);
  const t = useTranslations();
  const [inviteState, setInviteState] = useState<InviteState>({ status: "loading" });
  const [succeeded, setSucceeded] = useState(false);

  useEffect(() => {
    let isActive = true;

    const loadInvite = async () => {
      try {
        const response = await apiFetchClient(endpoints.public.invite(token), {
          cache: "force-cache",
        });

        if (!response.ok) {
          if (isActive) {
            setInviteState({ status: "invalid" });
          }
          return;
        }

        const data = (await response.json()) as PublicInviteResponse;
        if (isActive) {
          setInviteState({ status: "ready", data });
        }
      } catch {
        if (isActive) {
          setInviteState({ status: "invalid" });
        }
      }
    };

    void loadInvite();

    return () => {
      isActive = false;
    };
  }, [token]);

  if (succeeded) {
    return <InviteSuccessScreen loginHref={getSellerLoginUrl(locale)} />;
  }

  const bullets = [
    keys.invite.heroBulletOrders,
    keys.invite.heroBulletCatalog,
    keys.invite.heroBulletStorefront,
  ];

  return (
    <div className="flex min-h-svh flex-col bg-[color:var(--canvas)] lg:flex-row">
      <AuthBrandPanel
        greeting={t(keys.invite.heroGreeting)}
        title={t(keys.invite.heroTitle)}
        subtitle={t(keys.invite.heroSubtitle)}
      >
        <ul className="mt-2 flex flex-col gap-[18px]">
          {bullets.map((bulletKey) => (
            <li key={bulletKey} className="flex items-center gap-3.5 text-[16px] font-semibold">
              <BrandDiamondBullet />
              {t(bulletKey)}
            </li>
          ))}
        </ul>
      </AuthBrandPanel>

      <section className="flex flex-1 flex-col">
        <AuthMobileHeader
          greeting={t(keys.invite.heroGreeting)}
          title={t(keys.invite.heroTitle)}
          tagline={t(keys.invite.heroSubtitleCompact)}
        />

        <div className="flex flex-1 flex-col px-6 pb-7 pt-6 sm:px-10 lg:items-center lg:justify-center lg:px-12 lg:py-12">
          <div className="flex w-full max-w-[440px] flex-1 flex-col gap-6 lg:flex-none lg:gap-7">
            {inviteState.status === "loading" ? (
              <StatusMessage>{t(keys.common.loading)}</StatusMessage>
            ) : inviteState.status === "invalid" ? (
              <StatusMessage>{t(keys.invite.invalidLink)}</StatusMessage>
            ) : (
              <>
                <div className="hidden flex-col gap-2 lg:flex">
                  <h2 className="m-0 text-[28px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
                    {t(keys.invite.title)}
                  </h2>
                  <p className="m-0 text-[15px] leading-normal text-[color:var(--text-muted)]">
                    {t(keys.invite.subtitle)}
                  </p>
                </div>

                <IdentityChip data={inviteState.data} />

                <InviteAcceptForm token={token} onSuccess={() => setSucceeded(true)} />
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function IdentityChip({ data }: { data: PublicInviteResponse }) {
  const maskedContact = [data.sellerEmailMasked, data.sellerPhoneMasked]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="flex items-center gap-3 rounded-xl bg-[color:var(--surface-alt)] px-3.5 py-3 lg:gap-3.5 lg:px-4 lg:py-3.5">
      <div
        aria-hidden="true"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[color:var(--primary-500)] text-[14px] font-bold text-[color:var(--accent-500)] lg:h-11 lg:w-11 lg:text-[16px]"
      >
        {initials(data.sellerName)}
      </div>
      <div className="min-w-0">
        <div className="truncate text-[14px] font-bold text-[color:var(--text-primary)] lg:text-[15px]">
          {data.sellerName}
        </div>
        {maskedContact ? (
          <div className="truncate text-[12px] text-[color:var(--text-muted)] lg:text-[13px]">
            {maskedContact}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StatusMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-1 items-center justify-center py-12 text-sm text-[color:var(--text-muted)]">
      {children}
    </div>
  );
}
